import { html, type TemplateResult } from 'lit-html'
import {
  GAME_CONFIG,
  RELIC_GLYPH,
  SLICE_GLYPHS,
  UNKNOWN_GLYPH,
  validateLetterBody,
  type LetterBody,
  type LetterStatement,
  type api,
} from '@guild/core'

import type { App } from '../app.ts'
import { cellsOf, columnsClass, mapGrid, sliceGrid, type Cell } from '../map.ts'
import { letterSentOn, loadLetterSent, markLetterSent } from '../letter-sent.ts'
import type { Route } from '../route.ts'
import { defineScreen } from '../screen.ts'
import { focusSlip, practiceSlip, rememberFocus, restoreFocus, slip } from '../slip.ts'
import type { Store } from '../store.ts'
import { codeNameLine, errorText, fragmentText, letterProse, relicName } from '../text.ts'

/**
 * The letter (#55, rebuilt as steps for #97).
 *
 * A letter is built, not written (Q10.1): a category, a sentence, and every
 * blank in it filled from its own closed list. There is no field on this
 * screen a player can type prose into, and there is nothing here that checks
 * whether what they build is true -- lying is a mechanic, and the ground judges
 * it later.
 *
 * It is asked for one thing at a time, with a way back: the pattern to start
 * from, what you saw, the particulars, the enclosures, and then the signing.
 * The prose preview is on every step, because it is the thing being built and
 * a builder that hides its own result is a form.
 *
 * Two things this screen must get right and the server cannot:
 *
 *  - **which island the letter is about.** `dayAbout` travels with every save
 *    and every send, and the server refuses anything but today (Q17.1). A
 *    draft kept over midnight is therefore named by the day it describes,
 *    offered a fresh start and refused the seal, rather than being quietly
 *    relabelled. It is not put back on the screen: its coordinates are about
 *    ground that has gone under, and the copy the server holds is left
 *    untouched until the next save.
 *  - **the coordinates.** Slice anchors, relic claims and coordinate slots all
 *    count the way the map's margins count, x across and y down (#54).
 */

const { slice: sliceLimits, statementsPerLetter } = GAME_CONFIG.letter
const maxX = GAME_CONFIG.grid.width - 1
const maxY = GAME_CONFIG.grid.height - 1

type Picking =
  | { readonly kind: 'slice' }
  | { readonly kind: 'coord'; readonly statement: number; readonly slot: string }
  | null

type State = {
  loaded: boolean
  island: api.ApiIslandState | null
  /** The draft as the server holds it: the code name, and the day it is about. */
  draft: api.ApiDraft | null
  /** Set when the stored draft is about an island that is no longer today's. */
  stale: string | null
  body: LetterBody
  attachments: api.ApiAttachments
  fragmentIds: readonly string[]
  /** Which step is on show. */
  step: number
  picking: Picking
  /** The first corner of a slice, while the second is being chosen. */
  anchor: { x: number; y: number } | null
  selection: { x: number; y: number; w: number; h: number } | null
  altering: boolean
  alterGlyph: string
  sent: boolean
  pressing: boolean
  saved: boolean
  problem: string | null
  busy: boolean
  /** The practice shore's word on the letter, as a `ui` key, until it is read (#134). */
  note: string | null
  /**
   * Where the note leads once it is put down, when it leads anywhere (#137).
   *
   * On the practice shore the seal is where tomorrow begins, and a player
   * left on the sent sheet had only the mark beside the post to tell them
   * so. The slip that says it takes them to the morning itself.
   */
  onward: Route | null
}

const emptyBody: LetterBody = { opening: '', statements: [], closing: '' }

const initial: State = {
  loaded: false,
  island: null,
  draft: null,
  stale: null,
  body: emptyBody,
  attachments: {},
  fragmentIds: [],
  step: 0,
  picking: null,
  anchor: null,
  selection: null,
  altering: false,
  alterGlyph: UNKNOWN_GLYPH,
  sent: false,
  pressing: false,
  saved: false,
  problem: null,
  busy: false,
  note: null,
  onward: null,
}

// --- building a body -------------------------------------------------------

/** A template as a sentence to choose: its blanks shown as blanks. */
const blanked = (template: string): string => template.replace(/\{[^}]*\}/g, '…')

/** A template split into the words around its blanks and the blanks themselves. */
type Part = { readonly text: string } | { readonly slot: string }

function partsOf(template: string): Part[] {
  const parts: Part[] = []
  const pattern = /\{([A-Za-z0-9_]+)(?:\.[A-Za-z0-9_]+)?\}/g
  let last = 0
  for (const match of template.matchAll(pattern)) {
    const at = match.index
    if (at > last) parts.push({ text: template.slice(last, at) })
    parts.push({ slot: match[1] ?? '' })
    last = at + match[0].length
  }
  if (last < template.length) parts.push({ text: template.slice(last) })
  return parts
}

/**
 * A statement with every blank filled from the head of its own list, and any
 * coordinate starting where the player is standing.
 *
 * A body that is valid the moment it is chosen is a body the player edits
 * rather than assembles, and it keeps the preview honest from the first click.
 */
function fill(app: App, statementId: string, at: { x: number; y: number }): LetterStatement {
  const grammar = app.content.grammar(app.locale)
  const statement = grammar.statements.find((entry) => entry.id === statementId)
  const slots: Record<string, string | { x: number; y: number }> = {}
  for (const [name, type] of Object.entries(statement?.slots ?? {})) {
    if (type === 'coord') {
      slots[name] = { x: at.x, y: at.y }
      continue
    }
    const values = grammar.slots[type]
    const first = values?.[0]
    if (first !== undefined) slots[name] = first.id
  }
  return { statement: statementId, slots }
}

/** A body to start from: the first opening, one statement, the first closing. */
function startingBody(app: App, at: { x: number; y: number }): LetterBody {
  const grammar = app.content.grammar(app.locale)
  const first = grammar.statements[0]
  return {
    opening: grammar.openings[0]?.id ?? '',
    statements: first === undefined ? [] : [fill(app, first.id, at)],
    closing: grammar.closings[0]?.id ?? '',
  }
}

const isValid = (app: App, body: LetterBody): boolean =>
  validateLetterBody(body, app.content.grammar(app.locale)).ok

// --- the slice -------------------------------------------------------------

/**
 * The glyph the sender's own map shows for a tile.
 *
 * Not `cellsOf`, which draws the player where they stand: `@` is not a legal
 * slice glyph, and a slice is a claim about the ground rather than about who is
 * on it.
 */
function glyphAt(island: api.ApiIslandState, x: number, y: number): string {
  const relic = island.relics.find((marker) => marker.at.x === x && marker.at.y === y)
  if (relic !== undefined) return RELIC_GLYPH
  const cell = island.cells.find((entry) => entry.x === x && entry.y === y)
  return cell?.glyph ?? UNKNOWN_GLYPH
}

/** The slice as the API carries it, which owns its glyphs rather than sharing them. */
type Slice = NonNullable<api.ApiAttachments['slice']>

function cutSlice(
  island: api.ApiIslandState,
  rectangle: { x: number; y: number; w: number; h: number },
): Slice {
  const glyphs: string[] = []
  for (let y = rectangle.y; y < rectangle.y + rectangle.h; y += 1) {
    for (let x = rectangle.x; x < rectangle.x + rectangle.w; x += 1)
      glyphs.push(glyphAt(island, x, y))
  }
  return { ...rectangle, glyphs }
}

/** Two corners into a rectangle, clipped to what a slice may be (8 x 6). */
function rectangle(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number; w: number; h: number } {
  const x = Math.min(a.x, b.x)
  const y = Math.min(a.y, b.y)
  return {
    x,
    y,
    w: Math.min(Math.abs(a.x - b.x) + 1, sliceLimits.maxWidth),
    h: Math.min(Math.abs(a.y - b.y) + 1, sliceLimits.maxHeight),
  }
}

// --- the steps -------------------------------------------------------------

/**
 * One thing at a time.
 *
 * The order is the order a letter is thought of: what shape it takes, what it
 * says, exactly what it says, what travels with it, and whose hand it is
 * signed in. Each step's heading is a `ui` key; none of them is a sentence
 * held here.
 */
const STEPS: readonly {
  readonly key: string
  readonly render: (app: App, store: Store<State>, state: State) => TemplateResult
}[] = [
  { key: 'letterStartFrom', render: stepPattern },
  { key: 'stepSay', render: stepSay },
  { key: 'stepFill', render: stepFill },
  { key: 'enclosures', render: stepEnclosures },
  { key: 'stepSign', render: stepSign },
]

/** Moving between steps puts down whatever was being picked on the last one. */
const goToStep = (store: Store<State>, step: number): void => {
  store.set({
    step: Math.max(0, Math.min(STEPS.length - 1, step)),
    picking: null,
    altering: false,
    saved: false,
  })
}

// --- the screen ------------------------------------------------------------

export const letterScreen = defineScreen<State>({
  id: 'letter',
  initial,

  async enter(app, _route, store, current) {
    store.set({ problem: null, saved: false, pressing: false, step: 0 })
    const island = await app.api.island()
    if (!current()) return
    if (!island.ok) {
      if (island.status === 401) await app.setSession(null)
      if (current()) store.set({ loaded: true, problem: errorText(app, island) })
      return
    }
    const today = island.value

    const [draft, , atlas] = await Promise.all([
      app.api.draft(),
      // Shared with the island, which asks the same question when a day is
      // spent (#102): one request, one answer, no chance of disagreeing.
      loadLetterSent(app, today.day),
      app.api.atlas(),
    ])
    // The player may have changed while all three were in flight, and the save
    // below would be written under whoever signed in next (#115).
    if (!current()) return

    // A draft that failed to arrive is not the same fact as no draft: the
    // server may be holding one. Everything below reads `stored` as "there is
    // nothing kept", so a failure must never reach it (#110).
    const stored = draft.ok ? draft.value.draft : null
    const stale = stored !== null && stored.dayAbout !== today.day ? stored.dayAbout : null

    const sent = letterSentOn(today.day)
    store.set({
      loaded: true,
      island: today,
      draft: stored,
      stale,
      fragmentIds: atlas.ok ? atlas.value.fragmentIds : [],
      sent,
      body: stored !== null && stale === null ? stored.body : startingBody(app, today.position),
      attachments: stored !== null && stale === null ? stored.attachments : {},
      selection: null,
      anchor: null,
      picking: null,
      problem: draft.ok ? null : errorText(app, draft),
    })

    // The code name is drawn with the draft, and a sender should see what they
    // are about to sign before they seal it -- so an opening body that is
    // already valid is written down once, which is what gives it a name. A
    // draft is not a sent letter and is overwritten by every save after this.
    //
    // Only when the server actually said there is none. `saveDraft` updates
    // the row in place, so an opening body written over a failed fetch would
    // replace a kept letter with a blank one, on the connection least able to
    // say so (#110).
    if (draft.ok && stored === null && !sent && isValid(app, store.get().body)) {
      await save(app, store)
    }
    if (!current()) return

    // The practice shore's word on the letter, once, on the day it is written (#134).
    const note = sent ? null : practiceSlip(app, 'practiceLetter')
    if (note !== null) {
      rememberFocus()
      store.set({ note })
      focusSlip()
    }
  },

  keydown(event, state, app, store) {
    // The slip is read and put down; it never holds the sheet for long.
    if (state.note !== null && event.key === 'Escape') {
      event.preventDefault()
      putDown(app, store)
    }
  },

  render(state, app, store) {
    if (state.island === null) {
      return html`<section class="screen">
        ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      </section>`
    }
    if (state.sent) {
      return html`<section class="screen screen-sheet">
        <p class="label">${app.t('letterHeading')}</p>
        ${note(app, store, state)}
        <p class="note">${app.t('letterOneADay')}</p>
        <button class="verb verb-end" @click=${() => app.go({ screen: 'post', publicId: null })}>
          [ ${app.t('toThePost')} ]
        </button>
      </section>`
    }

    const valid = isValid(app, state.body)
    const step = STEPS[state.step] ?? STEPS[0]
    if (step === undefined) return html`<section class="screen"></section>`

    return html`<section class="screen screen-sheet">
      <p class="label">${app.t('letterHeading')}</p>
      ${note(app, store, state)} ${state.stale === null ? null : staleDraft(app, store, state)}

      <div class="sheet">
        <p class="salutation">${app.t('letterRecipientLine')}</p>

        <div class="preview">
          <p class="prose">${valid ? letterProse(app, state.body) : null}</p>
        </div>

        <div class="step-head">
          <p class="label">
            ${app.t(step.key)} · ${app.t('stepOf', { n: state.step + 1, total: STEPS.length })}
          </p>
          <div class="row">
            <button
              class="verb"
              ?disabled=${state.step === 0}
              @click=${() => goToStep(store, state.step - 1)}
            >
              [ ${app.t('stepBack')} ]
            </button>
            <button
              class="verb"
              ?disabled=${state.step === STEPS.length - 1}
              @click=${() => goToStep(store, state.step + 1)}
            >
              [ ${app.t('stepNext')} ]
            </button>
          </div>
        </div>

        <div class="step">${step.render(app, store, state)}</div>

        ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      </div>
    </section>`
  },
})

/**
 * The practice shore's slip over the sheet (#134): what a letter is, before
 * the first one; what the seal did, after it. Its own verb puts it down, and
 * so does Escape.
 */
function note(app: App, store: Store<State>, state: State): TemplateResult | null {
  if (state.note === null) return null
  return slip(app, {
    body: app.t(state.note),
    verbs: [{ key: 'foundNoted', primary: true, press: () => putDown(app, store) }],
  })
}

/** Put the slip down, and go where it led if it led anywhere (#137). */
function putDown(app: App, store: Store<State>): void {
  const { onward } = store.get()
  store.set({ note: null, onward: null })
  if (onward !== null) {
    app.go(onward)
    return
  }
  restoreFocus()
}

/**
 * Q17.1: a draft kept over midnight is said out loud, never relabelled.
 *
 * The old body is not shown. Its coordinates describe an island that has sunk,
 * so putting it back on today's map would be a letter about ground the player
 * cannot see; what the banner names is the draft on the server, which is left
 * as it is until the next save replaces it.
 */
function staleDraft(app: App, store: Store<State>, state: State): TemplateResult {
  return html`<div class="notes">
    <p class="note">${app.t('draftAboutAnotherIsland', { day: state.stale ?? '' })}</p>
    <button
      class="verb verb-end"
      @click=${() => {
        const island = state.island
        if (island === null) return
        store.set({
          stale: null,
          body: startingBody(app, island.position),
          attachments: {},
          selection: null,
          anchor: null,
          step: 0,
        })
      }}
    >
      [ ${app.t('draftStartAgain')} ]
    </button>
  </div>`
}

// --- step one: the pattern -------------------------------------------------

/**
 * The first control a player meets, which used to be a blank select (#126).
 *
 * The empty option is the one the body starts on -- a starting body is the
 * first opening, the first statement and the first closing, which is no
 * pattern at all -- and the one a body edited out of a pattern lands on
 * again. It says so: the letter is nobody's pattern but the writer's (Q24.2,
 * #128). The step's own heading names the step, so the field is not labelled
 * twice.
 */
function stepPattern(app: App, store: Store<State>, state: State): TemplateResult {
  const grammar = app.content.grammar(app.locale)
  const chosen = presetOf(state, grammar)
  return html`<p class="row">
    <select
      aria-label=${app.t('letterStartFrom')}
      @change=${(event: Event) => {
        const id = (event.target as HTMLSelectElement).value
        const preset = grammar.presets.find((entry) => entry.id === id)
        if (preset === undefined) return
        store.set({
          body: {
            opening: preset.opening,
            statements: preset.statements.map((statement) => ({
              statement: statement.statement,
              slots: { ...statement.slots },
            })),
            closing: preset.closing,
          },
        })
      }}
    >
      <option value="" ?selected=${chosen === ''}>${app.t('letterPatternNone')}</option>
      ${grammar.presets.map(
        (preset) =>
          html`<option value=${preset.id} ?selected=${preset.id === chosen}>
            ${preset.label}
          </option>`,
      )}
    </select>
  </p>`
}

/** Which pattern the body still matches, if any, so the picker shows it. */
function presetOf(state: State, grammar: ReturnType<App['content']['grammar']>): string {
  const same = grammar.presets.find(
    (preset) =>
      preset.opening === state.body.opening &&
      preset.closing === state.body.closing &&
      preset.statements.length === state.body.statements.length &&
      preset.statements.every(
        (statement, at) => statement.statement === state.body.statements[at]?.statement,
      ),
  )
  return same?.id ?? ''
}

// --- step two: what you saw ------------------------------------------------

function stepSay(app: App, store: Store<State>, state: State): TemplateResult {
  const grammar = app.content.grammar(app.locale)
  const body = state.body
  const left = statementsPerLetter.max - body.statements.length

  return html`<div class="builder">
    ${body.statements.map((filled, at) => {
      const statement = grammar.statements.find((entry) => entry.id === filled.statement)
      const category = statement?.category ?? grammar.categories[0]?.id
      const inCategory = grammar.statements.filter((entry) => entry.category === category)

      return html`<div class="statement">
        <p class="row">
          <select
            aria-label=${app.t('letterBuildStatement')}
            @change=${(event: Event) => {
              const id = (event.target as HTMLSelectElement).value
              const first = grammar.statements.find((entry) => entry.category === id)
              const island = state.island
              if (first === undefined || island === null) return
              replaceStatement(store, state, at, fill(app, first.id, island.position))
            }}
          >
            ${grammar.categories.map(
              (entry) =>
                html`<option value=${entry.id} ?selected=${entry.id === category}>
                  ${entry.label}
                </option>`,
            )}
          </select>
          <select
            aria-label=${app.t('letterBuildStatement')}
            @change=${(event: Event) => {
              const island = state.island
              if (island === null) return
              replaceStatement(
                store,
                state,
                at,
                fill(app, (event.target as HTMLSelectElement).value, island.position),
              )
            }}
          >
            ${inCategory.map(
              (entry) =>
                html`<option value=${entry.id} ?selected=${entry.id === filled.statement}>
                  ${blanked(entry.template)}
                </option>`,
            )}
          </select>
          ${
            body.statements.length > statementsPerLetter.min
              ? html`<button
                  class="quiet"
                  @click=${() =>
                    store.set({
                      body: {
                        ...body,
                        statements: body.statements.filter((_entry, index) => index !== at),
                      },
                    })}
                >
                  ${app.t('letterRemoveStatement')}
                </button>`
              : null
          }
        </p>
      </div>`
    })}

    <p class="row">
      <span class="quiet-ink">${app.t('letterStatementsLeft', { n: left })}</span>
      ${
        left > 0
          ? html`<button
              class="quiet"
              @click=${() => {
                const island = state.island
                const first = grammar.statements[0]
                if (island === null || first === undefined) return
                store.set({
                  body: {
                    ...body,
                    statements: [...body.statements, fill(app, first.id, island.position)],
                  },
                })
              }}
            >
              ${app.t('letterAddStatement')}
            </button>`
          : null
      }
    </p>
  </div>`
}

const replaceStatement = (
  store: Store<State>,
  state: State,
  at: number,
  next: LetterStatement,
): void =>
  store.set({
    body: {
      ...state.body,
      statements: state.body.statements.map((entry, index) => (index === at ? next : entry)),
    },
  })

// --- step three: the particulars --------------------------------------------

function stepFill(app: App, store: Store<State>, state: State): TemplateResult {
  const grammar = app.content.grammar(app.locale)
  const island = state.island

  return html`<div class="builder">
    ${state.body.statements.map((filled, at) => {
      const statement = grammar.statements.find((entry) => entry.id === filled.statement)
      if (statement === undefined) return null
      return html`<p class="sentence">
        ${partsOf(statement.template).map((part) => {
          if ('text' in part) return part.text
          const type = statement.slots[part.slot]
          if (type === undefined) return null
          const value = filled.slots[part.slot]

          if (type === 'coord') {
            const coord = typeof value === 'object' ? value : null
            const picking =
              state.picking?.kind === 'coord' &&
              state.picking.statement === at &&
              state.picking.slot === part.slot
            return html`<button
              class="slot slot-coord ${picking ? 'slot-picking' : ''}"
              @click=${() =>
                store.set({
                  picking: picking ? null : { kind: 'coord', statement: at, slot: part.slot },
                  anchor: null,
                })}
            >
              ${coord === null ? app.t('pickOnTheMap') : `${coord.x}, ${coord.y}`}
            </button>`
          }

          const values = grammar.slots[type] ?? []
          const chosen = values.find((entry) => entry.id === value)
          return html`<select
            class="slot"
            aria-label=${chosen?.text ?? type}
            @change=${(event: Event) =>
              replaceStatement(store, state, at, {
                ...filled,
                slots: {
                  ...filled.slots,
                  [part.slot]: (event.target as HTMLSelectElement).value,
                },
              })}
          >
            ${values.map(
              (entry) =>
                html`<option value=${entry.id} ?selected=${entry.id === value}>
                  ${entry.text}
                </option>`,
            )}
          </select>`
        })}
      </p>`
    })}
    ${island !== null && state.picking?.kind === 'coord' ? picker(app, store, state, island) : null}
  </div>`
}

// --- step four: the enclosures ----------------------------------------------

function stepEnclosures(app: App, store: Store<State>, state: State): TemplateResult {
  const island = state.island
  if (island === null) return html`<div></div>`
  const { slice, relic, fragment } = state.attachments

  return html`<div class="enclosures">
    <div class="torn-row">
      <div class="torn-holder">
        ${
          slice === undefined
            ? html`<button
                class="verb"
                @click=${() =>
                  store.set({ picking: { kind: 'slice' }, anchor: null, selection: null })}
              >
                [ ${app.t('attachSlice')} ]
              </button>`
            : html`<div class="torn">${sliceGrid(slice)}</div>
                <p class="meta">
                  ${app.t('sliceAttached', { w: slice.w, h: slice.h })}
                  <button
                    class="quiet"
                    @click=${() => store.set({ altering: !state.altering, picking: null })}
                  >
                    ${app.t('alter')}
                  </button>
                  <button
                    class="quiet"
                    @click=${() =>
                      store.set({
                        attachments: without(state.attachments, 'slice'),
                        altering: false,
                      })}
                  >
                    ${app.t('letterRemoveStatement')}
                  </button>
                </p>
                ${state.altering ? alterPanel(app, store, state, slice) : null}`
        }
      </div>

      <div class="torn-holder">
        <p class="label">${app.t('attachRelic')}</p>
        <p class="row">
          <select
            aria-label=${app.t('attachRelic')}
            @change=${(event: Event) => {
              const relicId = (event.target as HTMLSelectElement).value
              if (relicId === '') {
                store.set({ attachments: without(state.attachments, 'relic') })
                return
              }
              store.set({
                attachments: {
                  ...state.attachments,
                  relic: { relicId, at: relic?.at ?? island.position },
                },
              })
            }}
          >
            <option value=""></option>
            ${app.content
              .relics(app.locale)
              .map(
                (entry) =>
                  html`<option value=${entry.id} ?selected=${entry.id === relic?.relicId}>
                    ${entry.name}
                  </option>`,
              )}
          </select>
        </p>
        ${
          relic === undefined
            ? null
            : html`<p class="row">
                <span class="glyph accent">${RELIC_GLYPH}</span>
                ${coordInput(app, relic.at.x, maxX, 'coordX', (x) =>
                  store.set({
                    attachments: {
                      ...state.attachments,
                      relic: { ...relic, at: { ...relic.at, x } },
                    },
                  }),
                )}
                ${coordInput(app, relic.at.y, maxY, 'coordY', (y) =>
                  store.set({
                    attachments: {
                      ...state.attachments,
                      relic: { ...relic, at: { ...relic.at, y } },
                    },
                  }),
                )}
                <span class="serif-note">${relicName(app, relic.relicId)}</span>
              </p>`
        }
      </div>

      <div class="torn-holder">
        <p class="label">${app.t('attachFragment')}</p>
        <select
          aria-label=${app.t('attachFragment')}
          @change=${(event: Event) => {
            const fragmentId = (event.target as HTMLSelectElement).value
            store.set({
              attachments:
                fragmentId === ''
                  ? without(state.attachments, 'fragment')
                  : { ...state.attachments, fragment: { fragmentId } },
            })
          }}
        >
          <option value=""></option>
          ${state.fragmentIds.map(
            (id) =>
              html`<option value=${id} ?selected=${id === fragment?.fragmentId}>
                ${fragmentText(app, id)}
              </option>`,
          )}
        </select>
      </div>
    </div>

    ${state.picking?.kind === 'slice' ? picker(app, store, state, island) : null}
  </div>`
}

// --- step five: sign and seal ------------------------------------------------

function stepSign(app: App, store: Store<State>, state: State): TemplateResult {
  const grammar = app.content.grammar(app.locale)
  const body = state.body
  const valid = isValid(app, body)

  return html`<div class="builder">
    <p class="row">
      <span class="label">${app.t('letterBuildOpening')}</span>
      <select
        aria-label=${app.t('letterBuildOpening')}
        @change=${(event: Event) =>
          store.set({ body: { ...body, opening: (event.target as HTMLSelectElement).value } })}
      >
        ${grammar.openings.map(
          (opening) =>
            html`<option value=${opening.id} ?selected=${opening.id === body.opening}>
              ${opening.text}
            </option>`,
        )}
      </select>
    </p>

    <p class="row">
      <span class="label">${app.t('letterBuildClosing')}</span>
      <select
        aria-label=${app.t('letterBuildClosing')}
        @change=${(event: Event) =>
          store.set({ body: { ...body, closing: (event.target as HTMLSelectElement).value } })}
      >
        ${grammar.closings.map(
          (closing) =>
            html`<option value=${closing.id} ?selected=${closing.id === body.closing}>
              ${closing.text}
            </option>`,
        )}
      </select>
    </p>

    <div class="signature-row">
      <span class="signature"
        >${state.draft === null ? null : codeNameLine(app, state.draft.codeNameId)}</span
      >
      <button class="quiet" ?disabled=${state.busy} @click=${() => void reroll(app, store)}>
        ${app.t('codeNameReroll')}
      </button>
    </div>
    <p class="hint">${app.t('letterNoName')}</p>

    <div class="row row-send">
      <button class="verb" ?disabled=${!valid || state.busy} @click=${() => void keep(app, store)}>
        [ ${app.t('keepDraft')} ]
      </button>
      <button
        class="seal-send"
        ?disabled=${!valid || state.busy || state.stale !== null}
        @click=${() => void send(app, store)}
      >
        <span>${app.t('sealAndSend')}</span>
        <span class="seal ${state.pressing ? 'seal-pressing' : ''}" aria-hidden="true">✦</span>
      </button>
    </div>
    ${state.saved ? html`<p class="note">${app.t('draftKept')}</p>` : null}
  </div>`
}

// --- picking on the map ------------------------------------------------------

/**
 * The map, on the step that asked for it.
 *
 * One picker serves both jobs: a coordinate slot on the particulars step takes
 * a single tile, and the enclosures step takes a rectangle by drag or by two
 * taps (Q7.5). The number boxes below are the same choice by keyboard: four
 * for a rectangle, two for a coordinate (#112).
 */
function picker(
  app: App,
  store: Store<State>,
  state: State,
  island: api.ApiIslandState,
): TemplateResult {
  const cells: Cell[] = cellsOf(island)
  return html`<div class="picker">
    <p class="hint">
      ${state.picking?.kind === 'slice' ? app.t('sliceHint') : app.t('pickOnTheMap')}
    </p>
    ${mapGrid(cells, {
      selection: state.selection ?? undefined,
      onPoint: (x, y, phase) => onPoint(store, state, island, x, y, phase),
    })}
    ${
      state.picking?.kind === 'slice'
        ? sliceInputs(app, store, state, island)
        : coordInputs(app, store, state, island)
    }
  </div>`
}

/**
 * The keyboard way to fill a coordinate blank (#112).
 *
 * The map is `aria-hidden` and answers only to a pointer, so without these a
 * keyboard or screen-reader user could never say anything but where they were
 * standing when the statement was chosen. Two boxes, counting the way the
 * map's margins count, writing the same `{ x, y }` into the slot that a tap
 * writes -- and `[ done ]` rather than closing on the first change, because a
 * coordinate is two numbers and typing the first is not finishing.
 */
function coordInputs(
  app: App,
  store: Store<State>,
  state: State,
  island: api.ApiIslandState,
): TemplateResult | null {
  const picking = state.picking
  if (picking === null || picking.kind !== 'coord') return null
  const held = state.body.statements[picking.statement]?.slots[picking.slot]
  const at = typeof held === 'object' ? held : island.position

  const write = (next: { x: number; y: number }): void => {
    const current = store.get()
    store.set({
      body: {
        ...current.body,
        statements: current.body.statements.map((entry, index) =>
          index === picking.statement
            ? { ...entry, slots: { ...entry.slots, [picking.slot]: next } }
            : entry,
        ),
      },
    })
  }

  return html`<p class="row">
    ${coordInput(app, at.x, maxX, 'coordX', (x) => write({ ...at, x }))}
    ${coordInput(app, at.y, maxY, 'coordY', (y) => write({ ...at, y }))}
    <button class="verb" @click=${() => store.set({ picking: null })}>
      [ ${app.t('pickerDone')} ]
    </button>
  </p>`
}

/** Editing the glyphs before sending. Nobody checks (Q4.6, `docs/rules.md` 5). */
function alterPanel(app: App, store: Store<State>, state: State, slice: Slice): TemplateResult {
  return html`<div class="alter">
    <p class="hint">${app.t('alterHint')}</p>
    <p class="row">
      ${SLICE_GLYPHS.map(
        (glyph) =>
          html`<button
            class="glyph-key ${glyph === state.alterGlyph ? 'glyph-key-on' : ''}"
            @click=${() => store.set({ alterGlyph: glyph })}
          >
            ${glyph}
          </button>`,
      )}
    </p>
    <div
      class="slice slice-editable ${columnsClass(slice.w)}"
      @click=${(event: Event) => {
        const target = event.target as HTMLElement | null
        const cell = Number(target?.dataset['cell'])
        if (!Number.isInteger(cell)) return
        const glyphs = [...slice.glyphs]
        glyphs[cell] = state.alterGlyph
        store.set({ attachments: { ...state.attachments, slice: { ...slice, glyphs } } })
      }}
    >
      ${slice.glyphs.map(
        (glyph, cell) => html`<button class="cell" data-cell=${cell}>${glyph}</button>`,
      )}
    </div>
  </div>`
}

/** The keyboard way to cut a slice: the same numbers the map's margins show. */
function sliceInputs(
  app: App,
  store: Store<State>,
  state: State,
  island: api.ApiIslandState,
): TemplateResult {
  const current = state.selection ?? { x: 0, y: 0, w: 1, h: 1 }
  const update = (patch: Partial<typeof current>) => {
    const next = { ...current, ...patch }
    store.set({
      selection: next,
      attachments: { ...state.attachments, slice: cutSlice(island, next) },
    })
  }
  return html`<p class="row">
    ${coordInput(app, current.x, maxX, 'coordX', (x) => update({ x }))}
    ${coordInput(app, current.y, maxY, 'coordY', (y) => update({ y }))}
    ${coordInput(app, current.w, sliceLimits.maxWidth, 'sliceWidth', (w) => update({ w }), 1)}
    ${coordInput(app, current.h, sliceLimits.maxHeight, 'sliceHeight', (h) => update({ h }), 1)}
    <button class="verb" @click=${() => store.set({ picking: null })}>
      [ ${app.t('pickerDone')} ]
    </button>
  </p>`
}

/**
 * One number box, with its name written above it (#126).
 *
 * The four boxes of the slice picker read 0, 0, 1, 1, and which was which was
 * only ever in the accessibility tree; `coordX`, `coordY`, `sliceWidth` and
 * `sliceHeight` say so out loud now. The label wraps the input, so it is the
 * box's accessible name as well and there is one place the words come from.
 */
function coordInput(
  app: App,
  value: number,
  max: number,
  key: string,
  onChange: (value: number) => void,
  min = 0,
): TemplateResult {
  return html`<label class="field field-coord">
    <span class="label">${app.t(key)}</span>
    <input
      class="coord-input"
      type="number"
      min=${min}
      max=${max}
      .value=${String(value)}
      @change=${(event: Event) => {
        const next = Number((event.target as HTMLInputElement).value)
        if (Number.isInteger(next) && next >= min && next <= max) onChange(next)
      }}
    />
  </label>`
}

/** Two taps or one drag, the same handler either way (Q7.5). */
function onPoint(
  store: Store<State>,
  state: State,
  island: api.ApiIslandState,
  x: number,
  y: number,
  phase: 'down' | 'move' | 'up',
): void {
  const picking = state.picking
  if (picking === null) return

  if (picking.kind === 'coord') {
    if (phase !== 'down') return
    const statements = state.body.statements.map((entry, index) =>
      index === picking.statement
        ? { ...entry, slots: { ...entry.slots, [picking.slot]: { x, y } } }
        : entry,
    )
    store.set({ body: { ...state.body, statements }, picking: null })
    return
  }

  if (phase === 'down') {
    if (state.anchor === null) {
      const box = rectangle({ x, y }, { x, y })
      store.set({ anchor: { x, y }, selection: box })
      return
    }
    // The second tap closes the rectangle the first one opened.
    const box = rectangle(state.anchor, { x, y })
    store.set({
      anchor: null,
      selection: box,
      attachments: { ...state.attachments, slice: cutSlice(island, box) },
    })
    return
  }

  if (state.anchor === null) return
  const box = rectangle(state.anchor, { x, y })
  if (phase === 'move') {
    store.set({ selection: box })
    return
  }
  // A drag that actually moved finishes on release; a tap waits for its pair.
  if (box.w === 1 && box.h === 1) return
  store.set({
    anchor: null,
    selection: box,
    attachments: { ...state.attachments, slice: cutSlice(island, box) },
  })
}

/** Attachments are optional fields, so removing one means rebuilding without it. */
function without(
  attachments: api.ApiAttachments,
  key: keyof api.ApiAttachments,
): api.ApiAttachments {
  const next: api.ApiAttachments = {}
  if (key !== 'slice' && attachments.slice !== undefined) next.slice = attachments.slice
  if (key !== 'relic' && attachments.relic !== undefined) next.relic = attachments.relic
  if (key !== 'fragment' && attachments.fragment !== undefined) next.fragment = attachments.fragment
  return next
}

// --- saving and sending ----------------------------------------------------

async function save(app: App, store: Store<State>): Promise<boolean> {
  const state = store.get()
  const island = state.island
  if (island === null) return false
  store.set({ busy: true, problem: null, saved: false })
  const result = await app.api.saveDraft({
    body: state.body,
    attachments: state.attachments,
    dayAbout: island.day,
  })
  if (!result.ok) {
    store.set({ busy: false, problem: errorText(app, result) })
    return false
  }
  store.set({ busy: false, draft: result.value.draft, stale: null })
  return true
}

async function keep(app: App, store: Store<State>): Promise<void> {
  if (await save(app, store)) store.set({ saved: true })
}

async function reroll(app: App, store: Store<State>): Promise<void> {
  // One at a time: two re-rolls in flight draw two names and the second one
  // to answer wins, which is the one thing a re-roll must not do (#118).
  if (store.get().busy) return
  // A re-roll needs a draft to re-roll the name of, so saving comes first.
  if (store.get().draft === null && !(await save(app, store))) return
  store.set({ busy: true })
  const result = await app.api.reroll()
  if (!result.ok) {
    store.set({ busy: false, problem: errorText(app, result) })
    return
  }
  const draft = store.get().draft
  store.set(
    draft === null
      ? { busy: false }
      : {
          busy: false,
          draft: {
            ...draft,
            codeNameId: result.value.codeNameId,
            moodBucket: result.value.moodBucket,
          },
        },
  )
}

async function send(app: App, store: Store<State>): Promise<void> {
  if (!(await save(app, store))) return
  const island = store.get().island
  if (island === null) return
  store.set({ pressing: true, busy: true })
  const result = await app.api.send(island.day)
  if (!result.ok) {
    store.set({ pressing: false, busy: false, problem: errorText(app, result) })
    return
  }
  // The seal is pressed, and then the letter is gone until tomorrow. The
  // island is told without asking, so a spent day stops offering the sheet.
  markLetterSent(island.day)
  window.setTimeout(
    () => {
      // On the practice shore the seal is where tomorrow begins, and the
      // slip says so (#134) -- and then leads to the morning, where the
      // course strip would in a real day (#137).
      const note = practiceSlip(app, 'practiceSent')
      const onward: Route | null = note === null ? null : { screen: 'island', day: null }
      store.set({ pressing: false, busy: false, sent: true, draft: null, note, onward })
      if (note !== null) focusSlip()
    },
    app.reducedMotion ? 0 : 600,
  )
}
